import type { VFC } from 'react';
import React, { useContext, useMemo } from 'react';

import type { EditorActions } from '@atlaskit/editor-core/actions';

import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';
import {
	ContentAppearanceContextProvider,
	useContentAppearance,
} from '@confluence/content-appearance/entry-points/ContentAppearanceContext.ts';
import { RendererExtensionContext } from '@confluence/content-renderer-extension-context/entry-points/RendererExtensionContext.ts';
import { ClassicEditorContext } from '@confluence/editor-features/entry-points/ClassicEditorContext.ts';
import { ExcerptIncludeLoader } from '@confluence/excerpt-include/entry-points/loader.ts';
import type {
	ExcerptIncludeProps,
	RendererWrapperProps,
} from '@confluence/excerpt-include/entry-points/types.ts';
import { createHeadingLink } from '@confluence/editor-heading-links/entry-points/createHeadingLink.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { CONTENT_HISTORY } from '@confluence/named-routes/entry-points/routes.ts';
import { useRouteName } from '@confluence/route-manager/entry-points/RouteState.ts';

type ExcerptIncludeExtensionHandlerProps = {
	Renderer: React.FC<any>;
	editorActions: EditorActions;
	isLivePage?: boolean;
	getHeadingLinkPath?: () => string | null;
};

/**
 * Extension handler for "excerpt-include" macro.
 *
 * This macro, when given a target page, will attempt to fetch and display
 * the first excerpt found on the target page.
 */
const ExcerptIncludeExtensionHandlerImpl: VFC<
	ExtensionHandlerProps & ExcerptIncludeExtensionHandlerProps
> = (props) => {
	const {
		contentId,
		spaceKey,
		extensionKey,
		node,
		mode,
		Renderer,
		isLivePage,
		getHeadingLinkPath,
	} = props;

	const {
		contentAppearance: { appearance },
	} = useContentAppearance();

	const RendererWrapper = useMemo(
		() =>
			({
				targetContentId,
				adf,
				mediaToken,
				isInsideOfInlineExtension,
				headingIdPrefix,
				getHeadingLink,
			}: RendererWrapperProps) => {
				let allowHeadingAnchorLinks;
				if (expValEquals('confluence_toc_included_content_exp', 'isEnabled', true)) {
					// Only override the heading link when we have a real path builder
					// (editor / live-page paths), where the platform default would
					// otherwise produce the *edit* URL. On the view-page renderer path
					// `getHeadingLinkPath` is undefined, so we omit the override and let
					// the platform default (`getCurrentUrlWithHash` — current page URL +
					// hash) apply, matching normal (non-included) headings.
					const resolvedGetHeadingLink =
						getHeadingLink ??
						(getHeadingLinkPath
							? (headingId: string) => createHeadingLink(getHeadingLinkPath, headingId) ?? ''
							: undefined);

					allowHeadingAnchorLinks = {
						allowNestedHeaderLinks: true,
						...(resolvedGetHeadingLink ? { getHeadingLink: resolvedGetHeadingLink } : {}),
					};
				}

				const rendererProps = {
					contentId: targetContentId,
					spaceKey,
					// Transform the adf data into something PageContentRenderer understands (matching with content API response)
					data: {
						content: {
							nodes: [
								{
									body: {
										dynamic: {
											representation: 'atlas_doc_format',
											value: JSON.stringify({ content: adf, type: 'doc' }), // Have to transform this back to a string :(
										},
									},
								},
							],
						},
					},
					appearance,
					hasInlineComments: false,
					mediaToken,
					headingIdPrefix,
					allowHeadingAnchorLinks,
					annotationProvider: undefined,
					allowAnnotations: false,
					isInsideOfInlineExtension,
				};
				return <Renderer {...rendererProps} />;
			},
		[spaceKey, appearance, Renderer, getHeadingLinkPath],
	);

	const componentProps: ExcerptIncludeProps = {
		mode,
		contentId,
		spaceKey,
		extensionKey,
		macroNode: node,
		RendererWrapper,
		isLegacyRenderer: true,
		isLivePage,
	};

	return <ExcerptIncludeLoader {...componentProps} />;
};

export const ExcerptIncludeExtensionHandler = (props: any): React.JSX.Element => {
	const { getExtensionHandlers, populateExtensionProviders, getHeadingLinkPath } =
		useContext(ClassicEditorContext);

	const rendererExtensionOptions = useMemo(
		() => ({
			getExtensionHandlers,
			populateExtensionProviders,
			getHeadingLinkPath,
		}),
		[getExtensionHandlers, populateExtensionProviders, getHeadingLinkPath],
	);

	// New version history doesn't store page widths, so always use default appearance
	// to avoid layout breakage when the actual source page is full-width
	const isVersionHistoryView = useRouteName({
		selector: (routeName: string | undefined) => routeName === CONTENT_HISTORY.name,
	});

	// ExcerptInclude use PageContentRenderer which depends on RendererExtensionContext
	// New version history doesn't store page widths, so skip ContentAppearanceContextProvider
	// in version history to avoid layout breakage when the actual source page is full-width
	return (
		<RendererExtensionContext.Provider value={rendererExtensionOptions}>
			{isVersionHistoryView ? (
				<ExcerptIncludeExtensionHandlerImpl {...props} getHeadingLinkPath={getHeadingLinkPath} />
			) : (
				<ContentAppearanceContextProvider contentId={props.contentId}>
					<ExcerptIncludeExtensionHandlerImpl {...props} getHeadingLinkPath={getHeadingLinkPath} />
				</ContentAppearanceContextProvider>
			)}
		</RendererExtensionContext.Provider>
	);
};
